/**
 * hop-ghi-chu-crm.tsx — Hộp ghi chú khách hàng, đọc và ghi THẲNG vào CRM.
 *
 * Dùng ở hai chỗ:
 *  - cuối tab Thông tin (cột phải màn Hội thoại): 5 mục mới nhất + "Xem tất cả";
 *  - tab Ghi chú trong hồ sơ lịch sử mua hàng: toàn bộ.
 *
 * Trước đây ghi chú lưu riêng ở ChatMQL, còn CRM có ô ghi chú của nó: hai
 * nơi, hai nội dung, nhân viên xem CRM không thấy gì đã ghi ở đây. Nay chỉ
 * còn một nguồn là CRM; mục mới nối vào cuối theo đúng quy ước đội đang dùng
 * ("Tên ngày DD-MM-YYYY" rồi nội dung). Hiển thị mới nhất lên đầu.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/misc'
import { apiError } from '@/lib/api-client'
import { useGhiChuCrm } from '@/hooks/use-orders'
import type { MucGhiChu } from '@/lib/ghi-chu-crm'

export function HopGhiChuCrm({
  phone, cacMuc, gioiHan, onXemTatCa, khungVien = true,
}: {
  phone: string
  /** Theo thứ tự CRM lưu (cũ → mới); hộp tự đảo để mới nhất lên đầu. */
  cacMuc: MucGhiChu[]
  /** Chỉ hiện N mục mới nhất; bỏ trống = hiện hết. */
  gioiHan?: number
  /** Có hàm này thì hiện nút "Xem tất cả" khi còn mục bị cắt. */
  onXemTatCa?: () => void
  khungVien?: boolean
}) {
  const ghi = useGhiChuCrm()
  const [noiDung, setNoiDung] = useState('')

  // Đổi khách thì bỏ nháp — tránh lưu nhầm ghi chú của khách trước.
  useEffect(() => { setNoiDung('') }, [phone])

  const luu = () => {
    const nd = noiDung.trim()
    if (!nd) { toast.error('Chưa nhập nội dung ghi chú.'); return }
    ghi.mutate({ phone, content: nd }, {
      onSuccess: () => { toast.success('Đã ghi vào CRM'); setNoiDung('') },
      onError: (err: unknown) => toast.error(apiError(err)),
    })
  }

  const moiNhatTruoc = [...cacMuc].reverse()
  const hien = gioiHan ? moiNhatTruoc.slice(0, gioiHan) : moiNhatTruoc
  const conCat = moiNhatTruoc.length - hien.length

  return (
    <div className={khungVien ? 'rounded-lg border' : ''}>
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-[11.5px] font-bold tracking-wide text-muted-foreground">
          GHI CHÚ KHÁCH HÀNG{cacMuc.length ? ` (${cacMuc.length})` : ''}
        </span>
        <span className="text-[10.5px] text-muted-foreground">ghi thẳng vào CRM</span>
      </div>
      <div className="space-y-2 px-3 py-2.5">
        <Textarea
          value={noiDung}
          onChange={(e) => setNoiDung(e.target.value)}
          placeholder="Khách quan tâm gì, hẹn gọi lại lúc nào…"
          className="min-h-[64px] text-[12.5px]"
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') luu() }}
        />
        <Button className="w-full" size="sm" onClick={luu} disabled={ghi.isPending || !phone}>
          <Save /> {ghi.isPending ? 'Đang ghi vào CRM…' : 'Lưu vào CRM'}
        </Button>
        {!phone && (
          <p className="text-[11px] text-muted-foreground">Khách chưa có số điện thoại nên chưa ghi được vào CRM.</p>
        )}
      </div>
      {cacMuc.length > 0 ? (
        <div className="divide-y border-t">
          {hien.map((m, i) => (
            <div key={`${m.ngay}-${i}`} className="px-3 py-2">
              <div className="mb-0.5 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                <b className="truncate text-foreground">{m.nguoiGhi || 'Không rõ người ghi'}</b>
                <span className="shrink-0">{m.ngay ? m.ngay.replace(/-/g, '/') : 'không rõ ngày'}</span>
              </div>
              <div className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed">{m.noiDung}</div>
            </div>
          ))}
          {conCat > 0 && onXemTatCa && (
            <button
              type="button"
              onClick={onXemTatCa}
              className="w-full px-3 py-2 text-center text-[11.5px] font-semibold text-primary hover:bg-accent"
            >
              Xem tất cả {cacMuc.length} ghi chú
            </button>
          )}
        </div>
      ) : (
        <p className="border-t px-3 py-3 text-center text-[11.5px] text-muted-foreground">Chưa có ghi chú nào trên CRM.</p>
      )}
    </div>
  )
}
